import { space, tok } from '@sunwebgroup/ui-components'
import { Button, InputStepper, Typography } from '@sunwebgroup/ui-components/uds'
import { sub } from 'date-fns'
import { partial } from 'ramda'
import * as React from 'react'
import { use } from 'react'

import { styled } from 'styled-components'
import {
  BABIES_DEFAULT_AGE,
  BABIES_MAX_AGE,
  BABIES_MIN_AGE,
  CHILDREN_DEFAULT_AGE,
  CHILDREN_MAX_AGE,
  CHILDREN_MIN_AGE,
} from '@/domain/shared/constants'
import { useIntl } from '@/intl'
import { CatalogueDetailsContext } from '@/scopes/common/components/CatalogueDetailsContext'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import DateDropdown from '../../DateDropdown'
import { useRoomsParticipantsDistributionStore } from './useParticipantsStore'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: ${space('primitives_space_8')};
`

const RoomTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${space('primitives_space_12')};
  padding: ${space('primitives_space_8')} 0;
`

const Item = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${space('primitives_space_12')};
  padding: ${space('primitives_space_8')} 0;
  justify-content: space-between;
  align-items: center;
`

const ItemDate = styled(Item)`
  flex-direction: column;
  align-items: flex-start;
  gap: ${space('primitives_space_8')};
`

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const RoomTitle = styled(Typography)`
  display: inline-flex;
  align-items: center;
  text-transform: capitalize;
`

const SubLabel = styled(Typography)`
  color: ${tok('semantics_content_secondary_color')};
`

const MAX_ADULTS = 19
const MAX_CHILDREN = 19
const MAX_BABIES = 19

export default function Room({ roomIndex }: { roomIndex: number }) {
  const intl = useIntl()

  const {
    setAdults,
    addChild,
    setChild,
    removeChild,
    addBaby,
    setBaby,
    removeBaby,
    deleteRoom,
    roomsParticipantDistribution,
  } = useRoomsParticipantsDistributionStore()

  const { isWinter } = use(CatalogueDetailsContext)

  const dateFormat = useConfiguration('dateFormat')
  const birthdateReferenceDate = new Date()
  const childBirthdateMin = sub(birthdateReferenceDate, { years: CHILDREN_MAX_AGE })
  const childBirthdateMax = sub(birthdateReferenceDate, { years: CHILDREN_MIN_AGE, days: 1 })
  const babyBirthdateMin = sub(birthdateReferenceDate, { years: BABIES_MAX_AGE })
  const babyBirthdateMax = sub(birthdateReferenceDate, { years: BABIES_MIN_AGE, days: 1 })

  const { adults, babies, children } = roomsParticipantDistribution[roomIndex]

  const isFirstRoom = roomIndex === 0

  const handleAdultsChange = partial(setAdults, [roomIndex])

  const handleSetChildrenDate = (index: number) => (date: Date) => {
    setChild(roomIndex, index, new Date(date))
  }

  const handleChildChange = (newValue: number) => {
    if (newValue > children.length) {
      addChild(roomIndex, sub(new Date(), { years: CHILDREN_DEFAULT_AGE, days: 1 }))
    }
    else if (newValue < children.length) {
      removeChild(roomIndex)
    }
  }

  const handleSetBabiesDate = (index: number) => (date: Date) => {
    setBaby(roomIndex, index, date)
  }

  const handleBabyChange = (newValue: number) => {
    if (newValue > babies.length) {
      addBaby(roomIndex, sub(new Date(), { years: BABIES_DEFAULT_AGE, days: 1 }))
    }
    else if (newValue < babies.length) {
      removeBaby(roomIndex)
    }
  }

  const handleRoomDeleteClick = () => deleteRoom(roomIndex)

  return (
    <Wrapper>
      <RoomTitleWrapper>
        <RoomTitle variant="body-l-bold-tight">
          {intl.formatMessage({
            id: isWinter
              ? 'quicksearch_filter_participants_apartment_singular'
              : 'quicksearch_filter_participants_room_singular',
          })}
          {' '}
          {roomIndex + 1}
        </RoomTitle>
        {!isFirstRoom && (
          <Button variant="transparentPrimary" size="S" onClick={handleRoomDeleteClick}>
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_delete_room',
            })}
          </Button>
        )}
      </RoomTitleWrapper>
      <Item data-qa={`adults-number-stepper-room-${roomIndex}`}>
        <LabelWrapper>
          <Typography variant="body-m-regular-tight">
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_adults',
            })}
          </Typography>
          <SubLabel variant="body-s-regular-tight">
            {intl.formatMessage(
              { id: 'quicksearch_filter_participants_adults_description' },
              { min_age: CHILDREN_MAX_AGE },
            )}
          </SubLabel>
        </LabelWrapper>
        <InputStepper
          initialValue={adults}
          size="M"
          minimumValue={isFirstRoom ? 1 : 0}
          maximumValue={MAX_ADULTS}
          onChange={handleAdultsChange}
          disableInput
        />
      </Item>
      <Item>
        <LabelWrapper>
          <Typography variant="body-m-regular-tight">
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_children',
            })}
          </Typography>
          <SubLabel variant="body-s-regular-tight">
            {intl.formatMessage(
              { id: 'quicksearch_filter_participants_minors_description' },
              { min_age: CHILDREN_MIN_AGE, max_age: CHILDREN_MAX_AGE - 1 },
            )}
          </SubLabel>
        </LabelWrapper>
        <InputStepper
          initialValue={children.length}
          size="M"
          maximumValue={MAX_CHILDREN}
          onChange={handleChildChange}
          disableInput
        />
      </Item>
      {children.map((date: Date, index: number) => (
        <ItemDate key={index}>
          <Typography variant="body-s-regular-tight">
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_birthdate_child',
            })}
            {' '}
            {index + 1}
          </Typography>
          <DateDropdown
            value={date}
            onValueChange={handleSetChildrenDate(index)}
            min={childBirthdateMin}
            max={childBirthdateMax}
            dateFormat={dateFormat}
          />
        </ItemDate>
      ))}
      <Item>
        <LabelWrapper>
          <Typography variant="body-m-regular-tight">
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_babies',
            })}
          </Typography>
          <SubLabel variant="body-s-regular-tight">
            {intl.formatMessage(
              { id: 'quicksearch_filter_participants_minors_description' },
              { min_age: BABIES_MIN_AGE, max_age: BABIES_MAX_AGE - 1 },
            )}
          </SubLabel>
        </LabelWrapper>
        <InputStepper
          initialValue={babies.length}
          size="M"
          maximumValue={MAX_BABIES}
          onChange={handleBabyChange}
          disableInput
        />
      </Item>
      {babies.map((date: Date, index: number) => (
        <ItemDate key={index}>
          <Typography variant="body-s-regular-tight">
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_birthdate_baby',
            })}
            {' '}
            {index + 1}
          </Typography>
          <DateDropdown
            value={date}
            onValueChange={handleSetBabiesDate(index)}
            min={babyBirthdateMin}
            max={babyBirthdateMax}
            dateFormat={dateFormat}
          />
        </ItemDate>
      ))}
    </Wrapper>
  )
}
